import { Fragment, memo, useCallback, useEffect, useState } from 'react';
import useStyles from './textOutlineStyles';
import { useDispatch, useSelector } from 'react-redux';
import { getActiveEffects, updateEffectParams } from 'modules/effects';
import { TextSpace } from '@picsart/rc/old/TextSettings';
import { getCurrentSceneId } from 'modules/scenes';
import { IOnSettingChangeArgument } from '@picsart/rc/types/containerComponents/TextSettings/types';
import { getKonvaLayer } from 'modules/canvas';
import effectToKonvaTextSettings from 'utils/konva/effectToKonvaTextSettings';
import { TextEffect } from 'types/Effects';
import { saveRevision } from 'modules/revisions';
import { ToolName } from 'types/AppState';
import { getCurrentTextId } from 'modules/currentTextId';
import { useTranslator } from '@picsart/rc/services/localization';

export default memo(({ mode }: { mode: 'outline' | 'shadow' | 'text' }) => {
  const layer = useSelector(getKonvaLayer);
  const currentSceneId = useSelector(getCurrentSceneId);
  const currentTextId = useSelector(getCurrentTextId);
  const activeEffects = useSelector(getActiveEffects);
  const translator = useTranslator();
  const classes = useStyles();
  const dispatch = useDispatch();

  const [values, setValues] = useState<Partial<TextEffect>>(null);

  useEffect(() => {
    activeEffects[0] &&
      setValues({
        textOpacity: Math.floor(activeEffects[0].textOpacity * 100) || 100,
        outlineSize: Math.floor(activeEffects[0].outlineSize * 100) || 0,
        outlineOpacity: Math.floor(activeEffects[0].outlineOpacity * 100) || 100,
        shadowOpacity: Math.floor(activeEffects[0].shadowOpacity * 100) || 100,
        shadowBlur: Math.floor(activeEffects[0].shadowBlur * 100) || 0,
        shadowOffsetX: activeEffects[0].shadowOffsetX || 0,
        shadowOffsetY: activeEffects[0].shadowOffsetY || 0
      });
  }, [currentTextId, activeEffects]);

  const onChange = useCallback(
    (parameter: keyof TextEffect) => ({
      value,
      type,
      isRecentlyValue,
      isFromSlider
    }: IOnSettingChangeArgument & { value: number }) => {
      setValues(curr => ({ ...curr, [parameter]: value }));
      const newValue = value / (type === 'percentage' ? 100 : 1);
      if (isFromSlider || isRecentlyValue) {
        const konvaObjects = activeEffects.map(effect => ({
          obj: layer.findOne(`#${effect.id}`),
          effect
        }));

        konvaObjects.forEach(obj => {
          const effectAttrs = effectToKonvaTextSettings({ ...obj.effect, [parameter]: newValue });
          obj.obj.setAttrs(effectAttrs);
        });

        layer.draw();
      }
      if (isRecentlyValue) {
        activeEffects
          .map(effect => effect.id)
          .forEach((id: string) => {
            dispatch(
              updateEffectParams(currentSceneId, id, {
                [parameter]: newValue
              })
            );
            dispatch(saveRevision(ToolName.Text));
          });
      }
    },
    [dispatch, currentSceneId, activeEffects, layer]
  );

  return values && activeEffects.length ? (
    <div className={classes.outline}>
      <div className={classes.inputsRange}>
        {mode === 'text' && (
          <Fragment>
            <TextSpace
              max={100}
              min={0}
              active={false}
              name={translator('Opacity')}
              type="percentage"
              value={values.textOpacity}
              onSettingsChange={onChange('textOpacity')}
            />
          </Fragment>
        )}

        {mode === 'outline' && (
          <Fragment>
            <TextSpace
              max={100}
              min={0}
              active={false}
              name={translator('Size')}
              type="percentage"
              value={values.outlineSize}
              onSettingsChange={onChange('outlineSize')}
            />

            <TextSpace
              max={100}
              min={0}
              active={false}
              name={translator('Opacity')}
              type="percentage"
              value={values.outlineOpacity}
              onSettingsChange={onChange('outlineOpacity')}
            />
          </Fragment>
        )}

        {mode === 'shadow' && (
          <Fragment>
            <TextSpace
              max={100}
              min={0}
              active={false}
              name={translator('Opacity')}
              type="percentage"
              value={values.shadowOpacity}
              onSettingsChange={onChange('shadowOpacity')}
            />

            <TextSpace
              max={50}
              min={-50}
              active={false}
              name={translator('Offset X')}
              type="0"
              value={values.shadowOffsetX}
              onSettingsChange={onChange('shadowOffsetX')}
            />

            <TextSpace
              max={50}
              min={-50}
              active={false}
              name={translator('Offset Y')}
              type="0"
              value={values.shadowOffsetY}
              onSettingsChange={onChange('shadowOffsetY')}
            />

            <TextSpace
              max={100}
              min={0}
              active={false}
              name={translator('Blur')}
              type="percentage"
              value={values.shadowBlur}
              onSettingsChange={onChange('shadowBlur')}
            />
          </Fragment>
        )}
      </div>
    </div>
  ) : null;
});
